/**
 * Query keys, the shared LootClient singleton, and queryOptions builders —
 * plain functions with no React in them, so hooks and non-React code paths
 * (LootClient.onSessionExpired, the imperative session mint) share one cache.
 */
import {
  type BalancePack,
  type BattleListResponse,
  type BattleResponse,
  type BattleStatus,
  type BoxCategory,
  canonicalTwitchLogin,
  type ForgeBoxListResponse,
  type ForgeBoxSort,
  type ForgeImagesResponse,
  type ForgeItemsResponse,
  type InventoryItemStatus,
  LIVE_BREAK_BOX_SEARCH_PAGE_MAX,
  LIVE_BREAK_BOX_VERSIONS_MAX,
  LIVE_BREAK_CHANNEL_LOOKUP_MAX,
  LIVE_BREAK_QUEUE_PAGE_MAX,
  type LiveBreak,
  type LiveBreakBlock,
  type LiveBreakBox,
  type LiveBreakBoxStats,
  type LiveBreakBoxVersion,
  type LiveBreakChannelListResponse,
  type LiveBreakEntry,
  type LiveBreakHistoryDetail,
  type LiveBreakMyEntry,
  type LiveBreakMyEntryFilter,
  type LiveBreakMyQueuedEntry,
  type LiveBreakPlaylistListResponse,
  type MyForgeResponse,
  parseTwitchLogin,
  type RealtimeTokenResponse,
  type ShippingAddressResponse,
  type StorefrontBanner,
  TWITCH_CHANNEL_LOOKUP_MAX,
  type TwitchChannel,
  type UnclaimedRevealResponse,
} from "@loot-os/api-contract";
import { type ErrorCode, LootApiError, LootClient } from "@loot-os/client";
import { verifyOpen } from "@loot-os/client/verify";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { fetchWithAnalyticsContext } from "./analyticsGate.js";
import { LOOT_PROXY_BASE, PROXIED_PUBLISHABLE_KEY } from "./config.js";
import { queryClient } from "./queryClient.js";
import { captureServerTime } from "./serverClock.js";

/** Everything under "private" is token-scoped: sign-out removes it in one sweep. */
export const qk = {
  tenant: ["tenant-config"] as const,
  authMe: ["auth-me"] as const,
  private: ["private"] as const,
  session: ["private", "loot-session"] as const,
  balance: ["private", "balance"] as const,
  lootMe: ["private", "loot-me"] as const,
  opens: ["private", "opens"] as const,
  inventory: (status: InventoryItemStatus | "all") => ["private", "inventory", status] as const,
  balancePacks: ["private", "balance-packs"] as const,
  // Banners depend on the visitor's spend band, so the key names the viewer ("anon" signed out)
  // and a sign-in or sign-out fetches the right set instead of serving the other one from cache.
  banners: (viewer: string) => ["banners", viewer] as const,
  purchase: (sid: string) => ["private", "purchase", sid] as const,
  boxes: ["boxes", "list"] as const,
  box: (id: string) => ["boxes", "detail", id] as const,
  verification: (openId: string) => ["fairness", "verification", openId] as const,
  seed: (date: string) => ["fairness", "seed", date] as const,
  // Battles. The detail carries `me`, so it is token-scoped. The lobby folds in the signed-in
  // shopper's own private battles, so its key names the viewer ("anon" when signed out) and a
  // sign-in or sign-out fetches the right page instead of serving the other one from cache.
  battles: (viewer: string, status?: BattleStatus) =>
    ["battles", "list", viewer, status ?? "all"] as const,
  battle: (battleId: string) => ["private", "battles", "detail", battleId] as const,
  myBattles: ["private", "battles", "mine"] as const,
  // The realtime token names the shopper it was minted for (their own notification channel), so
  // the key names the viewer ("anon" signed out) and a sign-in or sign-out mints a new one.
  realtimeTokens: ["private", "realtime-token"] as const,
  realtimeToken: (viewer: string) => ["private", "realtime-token", viewer] as const,
  unclaimedReveal: ["private", "unclaimed-reveal"] as const,
  shippingAddress: ["private", "shipping-address"] as const,
  favorites: ["private", "favorites"] as const,
  // Forge. The browse grid and the preset images are public; the item search (a session route)
  // and the shopper's own boxes are token-scoped.
  /**
   * The Forge's browse grid: a shopper's favorites are theirs, under "private", so sign-out sweeps
   * them; every other browse is anyone's.
   */
  forgeBoxes: (browse: ForgeBoxesBrowse) =>
    browse.favorites
      ? (["private", "forge", "boxes", browse] as const)
      : (["forge", "boxes", browse] as const),
  /** Every favorites browse of the Forge, which a heart's change refreshes. */
  forgeFavoriteBoxes: ["private", "forge", "boxes"] as const,
  forgeImages: ["forge", "images"] as const,
  forgeItems: (search: ForgeItemSearch) =>
    [
      "private",
      "forge",
      "items",
      search.q,
      search.minCents ?? null,
      search.maxCents ?? null,
      search.sort ?? null,
    ] as const,
  myForge: ["private", "forge", "mine"] as const,
  // Public: what any shopper may see of a player, so it survives a sign-out.
  profile: (endUserId: string) => ["profiles", endUserId] as const,
  // Live breaks (the fetchers are in the "live breaks" block at the end of this file). What any
  // visitor reads alike is public: the directory, the queue, the viewers, the pinned box versions
  // (by version id: a version never changes), the box search, the sponsored streams and Twitch's
  // channel status. One live break's detail carries the caller's own fields (`isViewerHost`,
  // `isViewerBlocked`, the host's `stats`), and the estimate the caller's Twitch priority, so both
  // are token-scoped, as are the caller's entries, their host figures and their Twitch link.
  liveBreaks: ["liveBreaks", "list"] as const,
  liveBreak: (liveBreakId: string) => ["private", "liveBreaks", "detail", liveBreakId] as const,
  liveBreakQueue: (liveBreakId: string) => ["liveBreaks", "queue", liveBreakId] as const,
  /** No `entryId` = the wait for a purchase made now. */
  liveBreakEstimate: (liveBreakId: string, entryId?: string) =>
    ["private", "liveBreaks", "estimate", liveBreakId, entryId ?? "purchase"] as const,
  /** Every entry of the caller's in one live break, both lists (`opened`, `queued`). */
  liveBreakMine: (liveBreakId: string) => ["private", "liveBreaks", "mine", liveBreakId] as const,
  liveBreakMyEntries: (liveBreakId: string, status: LiveBreakMyEntryFilter) =>
    ["private", "liveBreaks", "mine", liveBreakId, status] as const,
  liveBreakViewers: (liveBreakId: string) => ["liveBreaks", "viewers", liveBreakId] as const,
  liveBreakBoxVersion: (boxVersionId: string) =>
    ["liveBreaks", "box-version", boxVersionId] as const,
  liveBreakBoxSearch: (search: LiveBreakBoxSearch) =>
    [
      "liveBreaks",
      "box-search",
      search.liveBreakId ?? null,
      search.q?.trim() ?? "",
      search.category ?? null,
      search.minBoxPriceCents ?? null,
      search.maxBoxPriceCents ?? null,
    ] as const,
  liveBreakSponsoredStreams: ["liveBreaks", "sponsored-streams"] as const,
  // The widget's (M7): open live breaks by channel, read as everyone reads them, and every entry
  // the caller still has queued anywhere.
  /** Sorted and de-duplicated, as `twitchChannels` is. */
  liveBreaksByChannel: (logins: readonly string[]) =>
    ["liveBreaks", "by-channel", twitchLoginsKey(logins)] as const,
  liveBreakMyQueued: ["private", "liveBreaks", "queued-everywhere"] as const,
  liveBreakHost: ["private", "liveBreaks", "host"] as const,
  /** Under the host read's key, so everything that reads the host again reads this too. */
  liveBreakHostOpen: ["private", "liveBreaks", "host", "open"] as const,
  /** Sorted and de-duplicated, so the same channels in any order share one read. */
  twitchChannels: (logins: readonly string[]) =>
    ["twitch", "channels", twitchLoginsKey(logins)] as const,
  twitchMe: ["private", "twitch", "me"] as const,
  // The host's own reads (M6; the fetchers are in the "live breaks: hosting" block at the end of
  // this file): their lifetime per-box stats, what they hosted, their Blocks, and the playlists
  // and the box picker priced at a rate. Every one needs a session or prices for the caller, so
  // every one is token-scoped.
  liveBreakHostBoxStats: ["private", "liveBreaks", "host-box-stats"] as const,
  /** Both the history list and each hosted live break's detail. */
  liveBreakHosted: ["private", "liveBreaks", "hosted"] as const,
  liveBreakHostedList: ["private", "liveBreaks", "hosted", "list"] as const,
  liveBreakHostedDetail: (liveBreakId: string) =>
    ["private", "liveBreaks", "hosted", "detail", liveBreakId] as const,
  liveBreakBlocks: ["private", "liveBreaks", "blocks"] as const,
  /** Null = priced at the caller's own host rate. */
  liveBreakPlaylists: (rateBps: number | null) =>
    ["private", "liveBreaks", "playlists", rateBps ?? "own"] as const,
  liveBreakPicker: (search: LiveBreakPickerSearch) =>
    [
      "private",
      "liveBreaks",
      "picker",
      search.liveBreakId ?? null,
      search.rateBps ?? null,
      search.q?.trim() ?? "",
      search.category ?? null,
      search.minBoxPriceCents ?? null,
      search.maxBoxPriceCents ?? null,
      search.onlySelected === true,
      search.limit ?? null,
    ] as const,
  liveBreakPickerByIds: (
    ids: readonly string[],
    rateBps: number | null,
    liveBreakId: string | null,
  ) =>
    [
      "private",
      "liveBreaks",
      "picker-ids",
      liveBreakId,
      rateBps,
      [...ids].sort().join(","),
    ] as const,
};

/**
 * The one LootClient for the whole app. Its config is constant (the BFF
 * injects the real publishable key server-side), and a session 401 re-mints
 * through the same cache the session query uses.
 */
export const lootClient = new LootClient({
  publishableKey: PROXIED_PUBLISHABLE_KEY,
  baseUrl: LOOT_PROXY_BASE,
  // While the analytics gate is open, each call says so; see `analyticsGate.ts`.
  fetch: fetchWithAnalyticsContext,
  onSessionExpired: () => mintFreshSessionToken(),
});

/** Re-mint when less than a minute of validity remains. */
const EXPIRY_SLACK_MS = 60_000;

type SessionData = { token: string; expiresAtMs: number } | null;

export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: qk.session,
    // A failed mint means "signed out", not "try again" — and the client
    // already retries a 401 once itself, so never stack retries on top.
    retry: false,
    gcTime: 30 * 60_000,
    // The token's TTL is the staleTime: fresh until 60s before expiry.
    staleTime: (query) => {
      const data = query.state.data;
      return data ? Math.max(0, data.expiresAtMs - Date.now() - EXPIRY_SLACK_MS) : 0;
    },
    queryFn: async (): Promise<SessionData> => {
      const res = await fetch("/api/loot/session", { method: "POST" });
      if (!res.ok) {
        lootClient.setSessionToken(null);
        return null;
      }
      const body = (await res.json()) as { token: string; expiresAt: string };
      // Set the client token synchronously so callers can fetch right away
      // (consumers re-render from the query data afterwards).
      lootClient.setSessionToken(body.token);
      return { token: body.token, expiresAtMs: Date.parse(body.expiresAt) };
    },
  });

/**
 * Mint (or reuse) a Loot session token. fetchQuery returns the cached token
 * while it's inside its TTL and dedupes concurrent callers onto one POST.
 */
export async function mintSessionToken(): Promise<string | null> {
  try {
    const data = await queryClient.fetchQuery(sessionQueryOptions());
    return data?.token ?? null;
  } catch {
    return null;
  }
}

/**
 * For onSessionExpired: the server says the token is dead even if we still
 * think it's fresh (revocation), so force staleness before refetching.
 */
export async function mintFreshSessionToken(): Promise<string | null> {
  await queryClient.invalidateQueries({ queryKey: qk.session, refetchType: "none" });
  return mintSessionToken();
}

export interface ShopperUser {
  id: string;
  email: string;
  /**
   * What the Messenger boots with, minted by the platform: the shopper id and its Identity
   * Verification hash. null when this storefront has chat off (or an older BFF omitted it).
   */
  intercom: { userId: string; userHash: string } | null;
}

export const authMeQueryOptions = () =>
  queryOptions({
    queryKey: qk.authMe,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<ShopperUser | null> => {
      const res = await fetch("/api/auth/me");
      if (!res.ok) return null;
      const body = (await res.json()) as { shopper: ShopperUser };
      return body.shopper;
    },
  });

export const boxesQueryOptions = () =>
  queryOptions({
    queryKey: qk.boxes,
    queryFn: () => lootClient.listBoxes(),
    staleTime: 60_000,
  });

export const boxQueryOptions = (boxId: string) =>
  queryOptions({
    queryKey: qk.box(boxId),
    queryFn: () => lootClient.getBox(boxId),
    staleTime: 60_000,
  });

export const balanceQueryOptions = () =>
  queryOptions({
    queryKey: qk.balance,
    queryFn: () => lootClient.getBalance(),
    retry: false,
    staleTime: 15_000,
  });

export const lootMeQueryOptions = () =>
  queryOptions({
    queryKey: qk.lootMe,
    queryFn: () => lootClient.getMe(),
    retry: false,
    staleTime: 60_000,
  });

/** The signed-in shopper's one unclaimed box reveal (or null); the claim modal shows it. */
export const unclaimedRevealQueryOptions = () =>
  queryOptions({
    queryKey: qk.unclaimedReveal,
    queryFn: () => lootFetch<UnclaimedRevealResponse>("/me/reveals/unclaimed"),
    retry: false,
    staleTime: 0,
  });

/** The signed-in shopper's saved shipping address, or null; the account page's card. */
export const shippingAddressQueryOptions = () =>
  queryOptions({
    queryKey: qk.shippingAddress,
    queryFn: () => lootFetch<ShippingAddressResponse>("/me/shipping-address"),
    retry: false,
    staleTime: 60_000,
  });

/** A player as other shoppers see them; the profile dialog. A 404 is an answer, not a retry. */
export const profileQueryOptions = (endUserId: string) =>
  queryOptions({
    queryKey: qk.profile(endUserId),
    queryFn: () => lootClient.getProfile(endUserId),
    retry: false,
    staleTime: 30_000,
  });

/** Packs shoppers can buy: `priceCents` is real money (USD), `creditedCents` what lands in the balance. */
export const balancePacksQueryOptions = () =>
  queryOptions({
    queryKey: qk.balancePacks,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BalancePack[]> => {
      const res = await fetch("/api/loot/balance/packs");
      // Non-OK (the feature is disabled, or a transient failure) renders as
      // "no packs" rather than an error, as it always has.
      if (!res.ok) return [];
      const body = (await res.json()) as { packs: BalancePack[] };
      return body.packs;
    },
  });

/**
 * The banners live for this visitor, in the store's order (Storefront → Banners in the
 * dashboard). Decoration: a failure renders as no banners rather than an error.
 */
export const bannersQueryOptions = (viewer: string) =>
  queryOptions({
    queryKey: qk.banners(viewer),
    staleTime: 60_000,
    queryFn: async (): Promise<StorefrontBanner[]> => {
      const res = await fetch("/api/storefront/banners");
      if (!res.ok) return [];
      const body = (await res.json()) as { banners: StorefrontBanner[] };
      return body.banners;
    },
  });

export const opensInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.opens,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.listMyOpens({ cursor: pageParam ?? undefined, limit: 20 }),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 30_000,
  });

/**
 * The shopper's inventory, newest first. `status` is part of the key, so the page's filter is a
 * separate cached page rather than a refetch of the same one.
 */
export const inventoryInfiniteQueryOptions = (status: InventoryItemStatus | "all") =>
  infiniteQueryOptions({
    queryKey: qk.inventory(status),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.listMyInventory({
        ...(status === "all" ? {} : { status }),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 24,
      }),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 30_000,
  });

/** A sale moved the balance and flipped the units it named, in every status filter. */
export function invalidateAfterInventorySale(): void {
  void queryClient.invalidateQueries({ queryKey: ["private", "inventory"] });
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

export interface PurchaseStatus {
  status: "pending" | "completed" | "failed" | "refunded" | "disputed";
  /** What the pack credits, integer US cents. */
  creditedCents: number;
  /** The balance once the purchase completed, integer US cents; null while pending. */
  balanceCents: number | null;
}

export type PurchasePollResult =
  | { kind: "unauthorized" }
  | { kind: "retry"; attempt: number }
  | { kind: "status"; status: PurchaseStatus; attempt: number };

/** How many polls before giving up (~20s at 1500ms — covers slow webhooks). */
export const PURCHASE_POLL_LIMIT = 14;

export const purchasePollQueryOptions = (checkoutSessionId: string) =>
  queryOptions({
    queryKey: qk.purchase(checkoutSessionId),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    // Each result carries its attempt number so both the interval callback
    // and the page can see the poll limit without reaching into query state.
    queryFn: async (): Promise<PurchasePollResult> => {
      const attempt =
        (queryClient.getQueryState(qk.purchase(checkoutSessionId))?.dataUpdateCount ?? 0) + 1;
      const res = await fetch(
        `/api/loot/balance/purchase?sessionId=${encodeURIComponent(checkoutSessionId)}`,
      );
      if (res.status === 401) return { kind: "unauthorized" };
      // Any other non-OK (5xx) keeps polling rather than terminating.
      if (!res.ok) return { kind: "retry", attempt };
      return { kind: "status", status: (await res.json()) as PurchaseStatus, attempt };
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 1500;
      if (data.kind === "unauthorized") return false;
      if (data.kind === "status" && data.status.status !== "pending") return false;
      if (data.attempt >= PURCHASE_POLL_LIMIT) return false;
      return 1500;
    },
  });

export const verificationQueryOptions = (openId: string) =>
  queryOptions({
    queryKey: qk.verification(openId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const bundle = await lootClient.getOpenVerification(openId);
      // The WebCrypto recompute is deterministic, so caching the pair is sound.
      const result = await verifyOpen(bundle);
      return { bundle, result };
    },
  });

export const seedQueryOptions = (date: string) =>
  queryOptions({
    queryKey: qk.seed(date),
    queryFn: () => lootClient.getSeed(date),
    staleTime: 60_000,
  });

/**
 * In reveal mode the open record and nonce advance at open, and the price moves into
 * `heldCents`, so the spendable balance changes here; the claim moves `balanceCents`.
 */
export function invalidateAfterOpen(): void {
  void queryClient.invalidateQueries({ queryKey: qk.opens });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
  void queryClient.invalidateQueries({ queryKey: qk.balance });
}

export function invalidateAfterClaim(): void {
  invalidateAfterOpen();
}

// ---- battles --------------------------------------------------------------------------

/**
 * `/v1` calls the `LootClient` has no method for, through the same proxy with the same session
 * token and the same one-shot re-mint on a 401 (the client's own behaviour). Throws
 * `LootApiError`, so `isLootApiErrorLike` and `err.code` work as they do for client calls.
 */
export async function lootFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const request = () => {
    const headers = new Headers(init.headers);
    const token = lootClient.getSessionToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
    headers.set("accept", "application/json");
    return fetch(`${LOOT_PROXY_BASE}/v1${path}`, { ...init, headers });
  };
  let res = await request();
  if (res.status === 401 && lootClient.getSessionToken()) {
    const fresh = await mintFreshSessionToken();
    if (fresh) res = await request();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => undefined)) as
      | { error?: { code?: ErrorCode; message?: string; details?: Record<string, unknown> } }
      | undefined;
    throw new LootApiError(
      body?.error?.code ?? "internal",
      body?.error?.message ?? `request failed with status ${res.status}`,
      res.status,
      body?.error?.details,
    );
  }
  // A 204 has no body to parse; the caller's `T` is `void` or `undefined` for those routes.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * The lobby: public battles plus, with a session, the shopper's own private ones (the session
 * token rides along). Polls every 5 s when there is no socket to tell us about changes.
 */
export const battlesQueryOptions = (opts: {
  viewer: string;
  status?: BattleStatus;
  poll: boolean;
}) =>
  queryOptions({
    queryKey: qk.battles(opts.viewer, opts.status),
    queryFn: async () => {
      const res = await lootFetch<BattleListResponse>(
        `/battles?limit=50${opts.status ? `&status=${opts.status}` : ""}`,
      );
      captureServerTime(res.serverTime);
      return res;
    },
    staleTime: 5_000,
    refetchInterval: opts.poll ? 5_000 : false,
  });

/**
 * One battle, gated by the server. Every fetch re-anchors the server clock. With no socket the
 * page polls once a second until the battle completes; a socket makes `poll` false.
 */
export const battleQueryOptions = (battleId: string, opts: { poll: boolean }) =>
  queryOptions({
    queryKey: qk.battle(battleId),
    queryFn: async () => {
      const res = await lootFetch<BattleResponse>(`/battles/${battleId}`);
      captureServerTime(res.serverTime);
      return res;
    },
    staleTime: 0,
    retry: false,
    refetchInterval: (query) => {
      if (!opts.poll) return false;
      const status = query.state.data?.battle.status;
      return status === "completed" ? false : 1_000;
    },
  });

export const myBattlesInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.myBattles,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootFetch<BattleListResponse>(
        `/me/battles?limit=20${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 30_000,
  });

/**
 * The ticket onto the stage's Event API; fresh until a minute before it expires. Minted with the
 * session when there is one (the BFF forwards it), so a signed-in viewer's token names them.
 */
export const realtimeTokenQueryOptions = (viewer: string) =>
  queryOptions({
    queryKey: qk.realtimeToken(viewer),
    retry: false,
    gcTime: 30 * 60_000,
    staleTime: (query) => {
      const data = query.state.data;
      return data ? Math.max(0, Date.parse(data.expiresAt) - Date.now() - 60_000) : 0;
    },
    queryFn: () => lootFetch<RealtimeTokenResponse>("/realtime/token", { method: "POST" }),
  });

/** After a seat is taken or a battle starts: the lists and, when given, that battle. */
export function invalidateAfterBattleChange(battleId?: string): void {
  void queryClient.invalidateQueries({ queryKey: ["battles", "list"] });
  void queryClient.invalidateQueries({ queryKey: qk.myBattles });
  if (battleId) void queryClient.invalidateQueries({ queryKey: qk.battle(battleId) });
}

/** Create and join debit the entry from the balance the moment the seat is taken. */
export function invalidateAfterBattleEntry(): void {
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

/** A claim minted items; a sell-back also credited the balance. */
export function invalidateAfterBattleClaim(battleId: string): void {
  invalidateAfterBattleChange(battleId);
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

// ---- forge ----------------------------------------------------------------------------

/** What the builder's item picker searches by: a name and a value range in US cents. */
export interface ForgeItemSearch {
  q: string;
  minCents?: number;
  maxCents?: number;
  /** By value, highest or lowest first; the server's own order (the bulk tiers first) when unset. */
  sort?: "value_desc" | "value_asc";
}

function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** What the Forge's browse grid asks for: a sort and its filters (`forgeBoxesQuerySchema`). */
export interface ForgeBoxesBrowse {
  sort: ForgeBoxSort;
  q?: string;
  minCents?: number;
  maxCents?: number;
  minVolatility?: number;
  maxVolatility?: number;
  favorites?: "true";
}

/** GET /v1/forge/boxes: the browse grid, by a sort and the filters, a page at a time. */
export const forgeBoxesInfiniteQueryOptions = (browse: ForgeBoxesBrowse) =>
  infiniteQueryOptions({
    queryKey: qk.forgeBoxes(browse),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootFetch<ForgeBoxListResponse>(
        `/forge/boxes${queryString({ ...browse, limit: 24, cursor: pageParam ?? undefined })}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
  });

/** GET /v1/forge/images: the store's preset box images; `[]` = the builder offers none. */
export const forgeImagesQueryOptions = () =>
  queryOptions({
    queryKey: qk.forgeImages,
    queryFn: () => lootFetch<ForgeImagesResponse>("/forge/images"),
    staleTime: 5 * 60_000,
  });

/**
 * GET /v1/forge/items: what a forge box may hold. The first page leads with the Mystery Bulk
 * Card tiers the search matches; later pages are the search alone.
 */
export const forgeItemsInfiniteQueryOptions = (search: ForgeItemSearch) =>
  infiniteQueryOptions({
    queryKey: qk.forgeItems(search),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootFetch<ForgeItemsResponse>(
        `/forge/items${queryString({
          q: search.q.trim() || undefined,
          minCents: search.minCents,
          maxCents: search.maxCents,
          sort: search.sort,
          limit: 48,
          cursor: pageParam ?? undefined,
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 60_000,
  });

/** GET /v1/me/forge: the shopper's own forge boxes and their unclaimed earnings. */
export const myForgeQueryOptions = () =>
  queryOptions({
    queryKey: qk.myForge,
    queryFn: () => lootFetch<MyForgeResponse>("/me/forge"),
    retry: false,
    staleTime: 15_000,
  });

/** A forge box was built, edited or had its status set: the grid and the shopper's list. */
export function invalidateAfterForgeChange(boxId?: string): void {
  void queryClient.invalidateQueries({ queryKey: ["forge", "boxes"] });
  void queryClient.invalidateQueries({ queryKey: qk.forgeFavoriteBoxes });
  void queryClient.invalidateQueries({ queryKey: qk.myForge });
  void queryClient.invalidateQueries({ queryKey: qk.boxes });
  if (boxId) void queryClient.invalidateQueries({ queryKey: qk.box(boxId) });
}

/** A claim credited the balance and zeroed the unclaimed earnings. */
export function invalidateAfterForgeClaim(): void {
  void queryClient.invalidateQueries({ queryKey: qk.myForge });
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

// ---- live breaks ----------------------------------------------------------------------------
//
// Every live-break read the storefront makes: the directory (`/live-breaks`), one live break's
// page (`/live-breaks/$liveBreakId`, its hooks in `lib/liveBreak*.ts`), and later the host pages
// and the widget. Each fetch that carries `serverTime` re-anchors the server clock, as the battle
// reads do. How events merge into these caches (a phase older than the cached one is dropped, a
// `queueKey` only ever decreases) is the realtime channel's; a queue read keeps the second rule
// too (`mergeLiveBreakQueueRead`), since a read that started before an event can land after it.
// A live-break read the pages need belongs here, beside these, rather than a `lootClient` call in
// a hook, so every reader shares one key and one cache.

/** How often the directory refetches while the tab is visible (and on every focus). */
export const LIVE_BREAKS_POLL_MS = 30_000;

/** GET /v1/live-breaks: the directory, newest first, 24 a page, polled every 30 s and on focus. */
export const liveBreaksInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.liveBreaks,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const page = await lootClient.liveBreaks.list(pageParam ? { cursor: pageParam } : {});
      captureServerTime(page.serverTime);
      return page;
    },
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 10_000,
    refetchInterval: LIVE_BREAKS_POLL_MS,
    // A tab in the background stops polling, so one created meanwhile shows on return.
    refetchOnWindowFocus: "always",
  });

/**
 * GET /v1/live-breaks/{id}: the live break, its boxes at live-break prices, the entry on stage and
 * the caller's own fields. A 404 is an answer, not a retry. `refetchInterval` is the page's poll
 * (5 s until the channel is subscribed, then 60 s).
 */
export const liveBreakQueryOptions = (
  liveBreakId: string,
  opts: { refetchInterval?: number | false } = {},
) =>
  queryOptions({
    queryKey: qk.liveBreak(liveBreakId),
    queryFn: async () => {
      const res = await lootClient.liveBreaks.get(liveBreakId);
      captureServerTime(res.serverTime);
      return res;
    },
    staleTime: 0,
    retry: false,
    refetchInterval: opts.refetchInterval ?? false,
  });

/** One live break's queue, every page of it: queued entries in queue order, the one on stage first. */
export interface LiveBreakQueueSnapshot {
  entries: LiveBreakEntry[];
  /** Entries still queued, the one on stage included. */
  queuedCount: number;
  serverTime: string;
}

/** The server's open order: ascending `queueKey`, compared as strings, then the id. */
function compareQueueOrder(a: LiveBreakEntry, b: LiveBreakEntry): number {
  const aKey = a.queueKey ?? "";
  const bKey = b.queueKey ?? "";
  if (aKey !== bKey) return aKey < bKey ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * A queue read merged into the cached queue (boxed's `mergeQueueRead`). The read decides which
 * entries are queued, but an entry whose cached `queueKey` is earlier keeps it, and its tier: a key
 * only ever decreases (a Pick, or Up Next protected from a jump), so a later one predates the move.
 * When any entry keeps its place the waiting entries are re-sorted and renumbered from 1, behind
 * the entry on stage (no place), which the queue always lists first whatever its key.
 */
export function mergeLiveBreakQueueRead(
  cached: readonly LiveBreakEntry[] | undefined,
  read: LiveBreakEntry[],
): LiveBreakEntry[] {
  if (!cached) return read;
  const known = new Map(cached.map((entry) => [entry.id, entry]));
  let moved = false;
  const merged = read.map((entry) => {
    const before = known.get(entry.id);
    if (!before?.queueKey || (entry.queueKey && entry.queueKey <= before.queueKey)) return entry;
    moved = true;
    return { ...entry, queueKey: before.queueKey, tier: before.tier };
  });
  if (!moved) return read;
  const onStage = merged.filter((entry) => entry.position === null);
  const waiting = merged.filter((entry) => entry.position !== null).sort(compareQueueOrder);
  return [...onStage, ...waiting.map((entry, index) => ({ ...entry, position: index + 1 }))];
}

/** GET /v1/live-breaks/{id}/queue, walked to its last page and merged into what is cached. */
export const liveBreakQueueQueryOptions = (liveBreakId: string) =>
  queryOptions({
    queryKey: qk.liveBreakQueue(liveBreakId),
    queryFn: async (): Promise<LiveBreakQueueSnapshot> => {
      const read: LiveBreakEntry[] = [];
      let cursor: string | undefined;
      let page: Awaited<ReturnType<typeof lootClient.liveBreaks.getQueue>>;
      do {
        page = await lootClient.liveBreaks.getQueue(liveBreakId, {
          ...(cursor ? { cursor } : {}),
          limit: LIVE_BREAK_QUEUE_PAGE_MAX,
        });
        read.push(...page.data);
        cursor = page.nextCursor ?? undefined;
      } while (cursor);
      captureServerTime(page.serverTime);
      const cached = queryClient.getQueryData<LiveBreakQueueSnapshot>(
        qk.liveBreakQueue(liveBreakId),
      );
      return {
        entries: mergeLiveBreakQueueRead(cached?.entries, read),
        queuedCount: page.queuedCount,
        serverTime: page.serverTime,
      };
    },
    staleTime: 0,
    retry: false,
  });

/**
 * GET /v1/live-breaks/{id}/estimate: the wait for `entryId`, or for a purchase made now (with a
 * session, its Twitch priority too). Null once the entry has left the queue or is on stage.
 */
export const liveBreakEstimateQueryOptions = (liveBreakId: string, entryId?: string) =>
  queryOptions({
    queryKey: qk.liveBreakEstimate(liveBreakId, entryId),
    queryFn: async () => {
      const res = await lootClient.liveBreaks.getEstimate(liveBreakId, entryId ? { entryId } : {});
      captureServerTime(res.serverTime);
      return res.estimate;
    },
    staleTime: 0,
    retry: false,
  });

/** Most entries one page of the caller's entries returns. */
const MY_ENTRIES_PAGE = 100;

/**
 * GET /v1/live-breaks/{id}/me/entries, every page: `opened` (the results, newest opened first) or
 * `queued` (in queue order). Needs a session; enable it only with one.
 */
export const liveBreakMyEntriesQueryOptions = (
  liveBreakId: string,
  status: LiveBreakMyEntryFilter,
) =>
  queryOptions({
    queryKey: qk.liveBreakMyEntries(liveBreakId, status),
    queryFn: async (): Promise<LiveBreakMyEntry[]> => {
      const entries: LiveBreakMyEntry[] = [];
      let cursor: string | undefined;
      do {
        const page = await lootClient.liveBreaks.listMyEntries(liveBreakId, {
          status,
          ...(cursor ? { cursor } : {}),
          limit: MY_ENTRIES_PAGE,
        });
        captureServerTime(page.serverTime);
        entries.push(...page.data);
        cursor = page.nextCursor ?? undefined;
      } while (cursor);
      return entries;
    },
    staleTime: 0,
    retry: false,
  });

/** GET /v1/live-breaks/{id}/viewers: the signed-in count and the newest viewers, for the roster. */
export const liveBreakViewersQueryOptions = (liveBreakId: string) =>
  queryOptions({
    queryKey: qk.liveBreakViewers(liveBreakId),
    queryFn: async () => {
      const res = await lootClient.liveBreaks.getViewers(liveBreakId);
      captureServerTime(res.serverTime);
      return res;
    },
    staleTime: 30_000,
    retry: false,
  });

/**
 * The pinned box versions `boxVersionIds` names, with their drop tables: from the cache where it
 * has them, the rest in one read per `LIVE_BREAK_BOX_VERSIONS_MAX`, each then cached by its id (a
 * version never changes), so `liveBreakBoxVersionQueryOptions` finds it. In the order asked; an id
 * the live break never pinned is left out.
 */
export async function fetchLiveBreakBoxVersions(
  liveBreakId: string,
  boxVersionIds: readonly string[],
): Promise<LiveBreakBoxVersion[]> {
  const wanted = [...new Set(boxVersionIds)];
  const cachedVersion = (id: string) =>
    queryClient.getQueryData<LiveBreakBoxVersion>(qk.liveBreakBoxVersion(id));
  const missing = wanted.filter((id) => cachedVersion(id) === undefined);
  for (let at = 0; at < missing.length; at += LIVE_BREAK_BOX_VERSIONS_MAX) {
    const res = await lootClient.liveBreaks.getBoxVersions(
      liveBreakId,
      missing.slice(at, at + LIVE_BREAK_BOX_VERSIONS_MAX),
    );
    for (const version of res.data) {
      queryClient.setQueryData(qk.liveBreakBoxVersion(version.id), version);
    }
  }
  return wanted.flatMap((id) => cachedVersion(id) ?? []);
}

/** One pinned box version and its drop table (an entry's `boxVersionId`), kept for the session. */
export const liveBreakBoxVersionQueryOptions = (liveBreakId: string, boxVersionId: string) =>
  queryOptions({
    queryKey: qk.liveBreakBoxVersion(boxVersionId),
    queryFn: async (): Promise<LiveBreakBoxVersion> => {
      const [version] = await fetchLiveBreakBoxVersions(liveBreakId, [boxVersionId]);
      if (!version) {
        throw new LootApiError("not_found", `no pinned box version ${boxVersionId}`, 404);
      }
      return version;
    },
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    retry: false,
  });

/** What the box search narrows by ("Want to open something else?", and the host's pickers). */
export interface LiveBreakBoxSearch {
  /** Price at this live break's rate, hold outside boxes to its minimum, and mark its own. */
  liveBreakId?: string;
  q?: string;
  category?: BoxCategory;
  /** Bounds on the box's own price, inclusive. */
  minBoxPriceCents?: number;
  maxBoxPriceCents?: number;
}

/** GET /v1/live-breaks/boxes: boxes a purchase would accept, priced as the live break sells them. */
export const liveBreakBoxSearchInfiniteQueryOptions = (search: LiveBreakBoxSearch) =>
  infiniteQueryOptions({
    queryKey: qk.liveBreakBoxSearch(search),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.liveBreaks.searchBoxes({
        ...search,
        q: search.q?.trim() || undefined,
        cursor: pageParam ?? undefined,
      }),
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    retry: false,
  });

/** GET /v1/live-breaks/sponsored-streams: the streamers staff show in the widget now. */
export const liveBreakSponsoredStreamsQueryOptions = () =>
  queryOptions({
    queryKey: qk.liveBreakSponsoredStreams,
    queryFn: () => lootClient.liveBreaks.listSponsoredStreams(),
    staleTime: 60_000,
    retry: false,
  });

/**
 * GET /v1/me/live-break-host: the caller as a host (`canHost`, rates, commission, all-time
 * figures, open live breaks). Needs a session; enable it only with one.
 */
export const liveBreakHostQueryOptions = () =>
  queryOptions({
    queryKey: qk.liveBreakHost,
    queryFn: async () => {
      const res = await lootClient.liveBreaks.getHost();
      captureServerTime(res.serverTime);
      return res.host;
    },
    staleTime: 60_000,
    retry: false,
  });

/**
 * GET /v1/me/live-breaks/hosting: the live breaks the caller hosts now, newest first, which is all
 * the widget's hosting row needs; it polls this, never the host read above with its figures.
 * Needs a session; enable it only with one.
 */
export const liveBreakHostOpenQueryOptions = () =>
  queryOptions({
    queryKey: qk.liveBreakHostOpen,
    queryFn: async (): Promise<LiveBreak[]> => {
      const res = await lootClient.liveBreaks.listHosting();
      captureServerTime(res.serverTime);
      return res.data;
    },
    staleTime: 30_000,
    retry: false,
  });

/** Twitch logins as one read asks for them: canonical, valid, de-duplicated and sorted. */
export function twitchLoginsOf(logins: readonly string[]): string[] {
  const valid = logins.flatMap((login) => parseTwitchLogin(login) ?? []);
  return [...new Set(valid)].sort();
}

function twitchLoginsKey(logins: readonly string[]): string {
  return twitchLoginsOf(logins.map(canonicalTwitchLogin)).join(",");
}

/**
 * GET /v1/twitch/channels for any number of logins, `TWITCH_CHANNEL_LOOKUP_MAX` a read, at most a
 * minute old. Enable it only while `twitchOf(config).enabled` and with a login to ask about: a
 * store without a Twitch app answers `twitch_not_enabled`.
 */
export const twitchChannelsQueryOptions = (logins: readonly string[]) =>
  queryOptions({
    queryKey: qk.twitchChannels(logins),
    queryFn: async (): Promise<TwitchChannel[]> => {
      const wanted = twitchLoginsOf(logins);
      const reads: Promise<{ data: TwitchChannel[] }>[] = [];
      for (let at = 0; at < wanted.length; at += TWITCH_CHANNEL_LOOKUP_MAX) {
        reads.push(lootClient.twitch.getChannels(wanted.slice(at, at + TWITCH_CHANNEL_LOOKUP_MAX)));
      }
      return (await Promise.all(reads)).flatMap((read) => read.data);
    },
    staleTime: 60_000,
    retry: false,
  });

/** GET /v1/me/twitch: whether the store has Twitch, and the caller's link. Needs a session. */
export const twitchMeQueryOptions = () =>
  queryOptions({
    queryKey: qk.twitchMe,
    queryFn: () => lootClient.twitch.getMe(),
    staleTime: 60_000,
    retry: false,
  });

// ---- live breaks: the widget (M7) ---------------------------------------------------------------
//
// The sitewide widget's own reads (`lib/useLiveBreaksWidget.ts`); it also reads the sponsored
// streams, Twitch's channels, the caller's host figures and one live break's detail above. Each is
// polled every minute by the widget (`refetchInterval` at its `useQuery`), not here, so the pages
// that share a key keep their own cadence.

/**
 * GET /v1/live-breaks/by-channel: the open public live breaks streaming on any of these logins,
 * `LIVE_BREAK_CHANNEL_LOOKUP_MAX` a read. Enable it only with a login to ask about.
 */
export const liveBreaksByChannelQueryOptions = (logins: readonly string[]) =>
  queryOptions({
    queryKey: qk.liveBreaksByChannel(logins),
    queryFn: async (): Promise<LiveBreak[]> => {
      const wanted = twitchLoginsOf(logins);
      const reads: Promise<LiveBreakChannelListResponse>[] = [];
      for (let at = 0; at < wanted.length; at += LIVE_BREAK_CHANNEL_LOOKUP_MAX) {
        reads.push(
          lootClient.liveBreaks.listByChannel(wanted.slice(at, at + LIVE_BREAK_CHANNEL_LOOKUP_MAX)),
        );
      }
      const pages = await Promise.all(reads);
      const last = pages.at(-1);
      if (last) captureServerTime(last.serverTime);
      return pages.flatMap((page) => page.data);
    },
    staleTime: 30_000,
    retry: false,
  });

/**
 * GET /v1/me/live-breaks/entries: every entry the caller still has queued, in every live break,
 * with its place. Needs a session; enable it only with one.
 */
export const liveBreakMyQueuedEntriesQueryOptions = () =>
  queryOptions({
    queryKey: qk.liveBreakMyQueued,
    queryFn: async (): Promise<LiveBreakMyQueuedEntry[]> => {
      const res = await lootClient.liveBreaks.listMyQueuedEntries();
      captureServerTime(res.serverTime);
      return res.data;
    },
    staleTime: 30_000,
    retry: false,
  });

/**
 * What the widget reads again when the shopper leaves a live break's page: a purchase, a Leave
 * queue or an End there may have changed their queue Return or hosting Return row (the host key
 * covers the hosting row's own read, `qk.liveBreakHostOpen`).
 */
export function invalidateLiveBreaksWidget(): void {
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakMyQueued });
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakHost });
}

/**
 * What one live break's page reads again on every (re)connect: the live break, its queue, the
 * caller's entries there (both lists) and their unclaimed reveal, which blocks a purchase.
 */
export function invalidateLiveBreakRoom(liveBreakId: string): void {
  void queryClient.invalidateQueries({ queryKey: qk.liveBreak(liveBreakId) });
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakQueue(liveBreakId) });
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakMine(liveBreakId) });
  void queryClient.invalidateQueries({ queryKey: qk.unclaimedReveal });
}

// ---- live breaks: hosting -----------------------------------------------------------------------
//
// The host's reads (M6): the host dashboard (`/live-breaks/host`), their history
// (`/live-breaks/host/history` and one hosted live break's), their Blocks, and what the create
// wizard and the room's settings price boxes with: the playlists and the box picker, each at a
// rate. Every one needs a session; enable each only with one.

/** GET /v1/me/live-break-host/box-stats: lifetime spend and opens per box, most viewer spend first. */
export const liveBreakHostBoxStatsQueryOptions = () =>
  queryOptions({
    queryKey: qk.liveBreakHostBoxStats,
    queryFn: async (): Promise<LiveBreakBoxStats[]> =>
      (await lootClient.liveBreaks.getHostBoxStats()).data,
    staleTime: 60_000,
    retry: false,
  });

/** GET /v1/me/live-break-host/live-breaks: what the caller hosted, newest first, 20 a page. */
export const liveBreakHostedInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.liveBreakHostedList,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.liveBreaks.listHostedLiveBreaks(pageParam ? { cursor: pageParam } : {}),
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    retry: false,
  });

/** GET /v1/me/live-break-host/live-breaks/{id}: one hosted live break with every stat. A 404 is an answer. */
export const liveBreakHostedDetailQueryOptions = (liveBreakId: string) =>
  queryOptions({
    queryKey: qk.liveBreakHostedDetail(liveBreakId),
    queryFn: async (): Promise<LiveBreakHistoryDetail> =>
      (await lootClient.liveBreaks.getHostedLiveBreak(liveBreakId)).liveBreak,
    staleTime: 30_000,
    retry: false,
  });

/** GET /v1/me/live-break-host/blocks: the shoppers the caller blocked, newest first. */
export const liveBreakBlocksQueryOptions = () =>
  queryOptions({
    queryKey: qk.liveBreakBlocks,
    queryFn: async (): Promise<LiveBreakBlock[]> => (await lootClient.liveBreaks.listBlocks()).data,
    staleTime: 30_000,
    retry: false,
  });

/**
 * GET /v1/live-breaks/playlists: staff's playlists priced at `rateBps` (null = the caller's own
 * host rate), each holding only the boxes a live break could sell today.
 */
export const liveBreakPlaylistsQueryOptions = (rateBps: number | null) =>
  queryOptions({
    queryKey: qk.liveBreakPlaylists(rateBps),
    queryFn: async (): Promise<LiveBreakPlaylistListResponse> =>
      lootClient.liveBreaks.listPlaylists(rateBps === null ? {} : { rateBps }),
    staleTime: 60_000,
    retry: false,
  });

/** What the host's box picker narrows by (the create wizard's boxes, Random, and Add boxes). */
export interface LiveBreakPickerSearch extends LiveBreakBoxSearch {
  /** An unsaved rate to price at, in basis points; omitted = the live break's, else the host's. */
  rateBps?: number;
  /** Only the live break's own boxes (needs `liveBreakId`). */
  onlySelected?: boolean;
  /** A page's size (up to `LIVE_BREAK_BOX_SEARCH_PAGE_MAX`); omitted = the API's default. */
  limit?: number;
}

/** GET /v1/live-breaks/boxes for the host: boxes a live break would take, priced at the draft rate. */
export const liveBreakPickerInfiniteQueryOptions = (search: LiveBreakPickerSearch) =>
  infiniteQueryOptions({
    queryKey: qk.liveBreakPicker(search),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.liveBreaks.searchBoxes({
        ...search,
        q: search.q?.trim() || undefined,
        onlySelected: search.onlySelected || undefined,
        cursor: pageParam ?? undefined,
      }),
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    retry: false,
  });

/**
 * GET /v1/live-breaks/boxes?ids=…: these boxes, eligible or not, priced at `rateBps` (null = the
 * live break's rate, else the host's): the picks the host already made, re-priced when the rate
 * they are drafting changes.
 */
export const liveBreakPickerByIdsQueryOptions = (
  ids: readonly string[],
  rateBps: number | null,
  liveBreakId: string | null,
) =>
  queryOptions({
    queryKey: qk.liveBreakPickerByIds(ids, rateBps, liveBreakId),
    queryFn: async (): Promise<LiveBreakBox[]> => {
      const read: LiveBreakBox[] = [];
      for (let at = 0; at < ids.length; at += LIVE_BREAK_BOX_SEARCH_PAGE_MAX) {
        const page = await lootClient.liveBreaks.searchBoxes({
          ids: ids.slice(at, at + LIVE_BREAK_BOX_SEARCH_PAGE_MAX),
          limit: LIVE_BREAK_BOX_SEARCH_PAGE_MAX,
          ...(rateBps === null ? {} : { rateBps }),
          ...(liveBreakId ? { liveBreakId } : {}),
        });
        read.push(...page.data);
      }
      return read;
    },
    staleTime: 30_000,
    retry: false,
  });

/** What a host's write moved on their own pages: the figures, the per-box stats and their history. */
export function invalidateLiveBreakHosting(): void {
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakHost });
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakHostBoxStats });
  void queryClient.invalidateQueries({ queryKey: qk.liveBreakHosted });
}
